import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { ApiClientError, getFriendlyErrorMessage } from "./api-client";
import { Notify } from "@/components/ui/notify";

// Meta tipado para as duas rotas de escape do tratamento de erro centralizado:
// mutation com UX própria (banner de conflito, botão "Forçar" etc.) pula o toast automático;
// query que não deve ir pro Sentry (ex.: sabidamente ruidosa) pula a captura automática.
declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      skipErrorToast?: boolean;
      errorTitle?: string;
    };
    queryMeta: {
      skipErrorSentry?: boolean;
    };
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Toda mutation sem tratamento próprio ganha um toast amigável de graça — o padrão seguro
    // não depende mais de cada desenvolvedor lembrar de escrever try/catch. Quem já trata o erro
    // localmente (banner de conflito, mensagem customizada) faz opt-out via meta.skipErrorToast.
    onError: (err, _vars, _ctx, mutation) => {
      if (mutation.meta?.skipErrorToast) return;
      Notify.toast({
        variant: "danger",
        title: mutation.meta?.errorTitle ?? "Falha ao salvar",
        description: getFriendlyErrorMessage(err),
      });
    },
  }),
  queryCache: new QueryCache({
    // Observabilidade incondicional: mesmo uma tela que ainda não foi migrada pra exibir o erro
    // (QueryErrorState) não fica mais invisível para o time — todo erro de leitura vai pro
    // Sentry. 401 é ruído esperado (token expirando, tratado em api-client.ts/auth.store).
    onError: (err, query) => {
      if (query.meta?.skipErrorSentry) return;
      if (err instanceof ApiClientError && err.status === 401) return;
      Sentry.captureException(err);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry(failureCount, error) {
        if (error instanceof ApiClientError) {
          if ([400, 401, 403, 404, 422].includes(error.status)) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
